import { useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ClockAlert } from 'lucide-react';
import { useToast } from '@kollez/design-system';
import { useConsumeSignInLink, useInspectSignInLink } from '@/lib/queries';
import { useAuthStore } from '@/lib/authStore';
import { landingFor } from '@/lib/roleHome';

/**
 * /signin-link — landed here by clicking the magic-link email. The page
 *   inspects the token, then auto-consumes it to sign the user in.
 *   Success → hydrate auth store → nav to role home.
 *   Failure → show one of LINK_EXPIRED / LINK_ALREADY_USED /
 *   LINK_INVALID with a "back to sign in" affordance.
 *
 * The token is single-use and short-lived (15 min). We consume as soon
 * as the inspect succeeds so a user forwarding the link doesn't
 * accidentally sign in twice.
 */
export function SignInLinkPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const nav = useNavigate();
  const toast = useToast();
  const setSession = useAuthStore((s) => s.setSession);

  const inspect = useInspectSignInLink(token);
  const consume = useConsumeSignInLink();

  useEffect(() => {
    if (!token || !inspect.data || consume.isPending || consume.data) return;
    consume.mutate(token, {
      onSuccess: (res) => {
        setSession(
          {
            id: res.user.id,
            fullName: res.user.fullName,
            email: res.user.email,
            role: res.user.role,
            tenant: res.user.tenant,
            onboardingComplete: res.user.onboardingComplete,
          },
          res.tokens,
          { activeTenantId: res.activeTenantId, memberships: [] },
        );
        toast.show({ message: `Signed in as ${res.user.email}.` });
        nav(landingFor(res.user.role));
      },
      onError: (err) => {
        toast.show({
          tone: 'danger',
          message: err instanceof Error ? err.message : 'Could not sign you in.',
        });
      },
    });
  }, [token, inspect.data, consume, nav, setSession, toast]);

  if (!token) {
    return errorView('No token in this link — try opening the email again.');
  }
  if (inspect.isLoading) {
    return (
      <div className="mx-auto" style={{ maxWidth: 560, padding: '48px 20px' }}>
        <p className="helper" data-testid="signin-link-inspecting">Checking your sign-in link…</p>
      </div>
    );
  }
  if (inspect.error) {
    const msg = inspect.error instanceof Error ? inspect.error.message : 'Link invalid.';
    return errorView(humanize(msg));
  }
  if (consume.isPending) {
    return (
      <div className="mx-auto" style={{ maxWidth: 560, padding: '48px 20px' }}>
        <p className="helper" data-testid="signin-link-consuming">
          Signing you in as {inspect.data?.email}…
        </p>
      </div>
    );
  }
  if (consume.error) {
    const msg = consume.error instanceof Error ? consume.error.message : 'Sign-in failed.';
    return errorView(humanize(msg));
  }
  // Success path — the effect already navigates away.
  return null;
}

function errorView(msg: string) {
  return (
    <div
      className="mx-auto"
      style={{ maxWidth: 560, padding: '48px 20px', display: 'grid', gap: 16 }}
      data-testid="signin-link-error"
    >
      <span className="logo-tile" style={{ width: 52, height: 52, color: 'rgb(var(--ink-warning))' }}>
        <ClockAlert size={20} />
      </span>
      <h1 className="h-page">This sign-in link didn't work</h1>
      <p className="m-0">{msg}</p>
      <p className="helper m-0">
        Ask for a fresh link at <Link className="link" to="/login">/login</Link>, or reach out to{' '}
        <a className="link" href="mailto:support@kollez.ai">support@kollez.ai</a>.
      </p>
      <Link className="btn btn-primary justify-self-start" to="/login">Go to sign in</Link>
    </div>
  );
}

function humanize(msg: string): string {
  if (/EXPIRED/i.test(msg)) return 'This link expired. Sign-in links are valid for 15 minutes.';
  if (/ALREADY_USED/i.test(msg)) return 'This link was already used. Request a new one.';
  if (/INVALID/i.test(msg)) return 'This link is invalid — it may be truncated or the token has changed.';
  return msg;
}
